/**
 * RSSContentCard Component
 *
 * Displays an RSS feed item in card format.
 * Shows source headline, summary, and "Select for Draft" action.
 * Enhanced with visual hierarchy and better affordances.
 */

import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

// Card styles
import { cardStyles } from "./cardStyles";

// Helper function to format date with relative time
const formatDate = (dateString) => {
  if (!dateString) return "Unknown date";
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));

  if (diffHours < 1) return "Just now";
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

// Get source icon based on channel
const getSourceIcon = (channel) => {
  const lowerChannel = (channel || "").toLowerCase();
  if (lowerChannel.includes("reddit")) return "forum";
  if (lowerChannel.includes("rss")) return "rss_feed";
  if (lowerChannel.includes("news")) return "newspaper";
  if (lowerChannel.includes("twitter") || lowerChannel.includes("x")) return "alternate_email";
  return "article";
};

// Get source color based on channel
const getSourceColor = (channel) => {
  const lowerChannel = (channel || "").toLowerCase();
  if (lowerChannel.includes("reddit")) return { bg: "rgba(255, 69, 0, 0.1)", text: "#FF4500" };
  if (lowerChannel.includes("rss")) return { bg: "rgba(255, 165, 0, 0.1)", text: "#F59E0B" };
  if (lowerChannel.includes("news")) return { bg: "rgba(26, 115, 232, 0.1)", text: "#1A73E8" };
  return { bg: "rgba(107, 114, 128, 0.1)", text: "#6B7280" };
};

function RSSContentCard({ item, onSelect, onClick, isProcessing = false }) {
  // Check if item has Processing status from Supabase
  const isStatusProcessing = item.status === "Processing";
  const showProcessing = isProcessing || isStatusProcessing;
  const isSelectable = item.status === "New" && !isProcessing;
  const sourceColors = getSourceColor(item.sourceChannel);
  const sourceIcon = getSourceIcon(item.sourceChannel);

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        borderRadius: "16px",
        position: "relative",
        overflow: "hidden",
        ...cardStyles.rss,
        opacity: showProcessing ? 0.85 : 1,
        cursor: showProcessing ? "default" : "pointer",
        "&:hover": {
          transform: showProcessing ? "none" : "translateY(-6px)",
          ...(!showProcessing && cardStyles.rss["&:hover"]),
        },
        "&:active": {
          transform: showProcessing ? "none" : "translateY(-2px)",
        },
        "@media (hover: none)": {
          "&:hover": {
            transform: "none",
          },
          "&:active": {
            transform: showProcessing ? "none" : "scale(0.98)",
          },
        },
      }}
    >
      {/* Processing indicator bar at top */}
      {showProcessing && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "3px",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#1A73E8",
            },
            backgroundColor: "rgba(26, 115, 232, 0.15)",
          }}
        />
      )}
      <CardContent
        sx={{
          flexGrow: 1,
          cursor: showProcessing ? "default" : "pointer",
          p: { xs: 2, sm: 2.5, md: 3 },
          pt: showProcessing ? { xs: 2.5, sm: 3, md: 3.5 } : { xs: 2, sm: 2.5, md: 3 },
          touchAction: "manipulation",
        }}
        onClick={() => !showProcessing && onClick(item)}
      >
        {/* Header with Channel Badge and Date */}
        <MDBox
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          {item.sourceChannel && (
            <MDBox
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.25,
                py: 0.4,
                borderRadius: "8px",
                backgroundColor: sourceColors.bg,
                color: sourceColors.text,
              }}
            >
              <Icon sx={{ fontSize: "0.9rem !important" }}>{sourceIcon}</Icon>
              <MDTypography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  color: "inherit",
                }}
              >
                {item.sourceChannel}
              </MDTypography>
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center" gap={0.5}>
            <Icon sx={{ fontSize: "0.85rem !important", color: "text.secondary", opacity: 0.6 }}>
              schedule
            </Icon>
            <MDTypography variant="caption" color="text" fontWeight="medium" sx={{ opacity: 0.7 }}>
              {formatDate(item.date)}
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* Source Headline */}
        <MDBox mb={2}>
          <MDTypography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            sx={{
              fontSize: { xs: "1.05rem", sm: "1.15rem", md: "1.25rem" },
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: "#1a1a2e",
            }}
          >
            {item.sourceHeadline}
          </MDTypography>
        </MDBox>

        {/* Source Summary */}
        <MDBox mb={1}>
          <MDTypography
            variant="body2"
            color="text"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.65,
              fontSize: { xs: "0.85rem", sm: "0.875rem" },
              color: "#4a4a68",
            }}
          >
            {item.sourceSummary}
          </MDTypography>
        </MDBox>

        {/* Source URL Preview */}
        {item.sourceURL &&
          (() => {
            try {
              const url = new URL(item.sourceURL);
              return (
                <MDBox
                  mt={2}
                  pt={2}
                  borderTop="1px solid"
                  borderColor="rgba(0,0,0,0.06)"
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <Icon
                    sx={{ fontSize: "0.85rem !important", color: "text.secondary", opacity: 0.5 }}
                  >
                    link
                  </Icon>
                  <MDTypography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      opacity: 0.7,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {url.hostname}
                  </MDTypography>
                </MDBox>
              );
            } catch (e) {
              // Invalid URL, don't render anything
              return null;
            }
          })()}
      </CardContent>

      {/* Actions */}
      <CardActions sx={{ p: { xs: 2, sm: 2.5, md: 3 }, pt: 0 }}>
        <MDButton
          variant={showProcessing ? "outlined" : "gradient"}
          color={showProcessing ? "secondary" : "info"}
          fullWidth
          disabled={!isSelectable}
          onClick={(e) => {
            e.stopPropagation();
            if (isSelectable) {
              onSelect(item);
            }
          }}
          startIcon={<Icon>{showProcessing ? "hourglass_empty" : "auto_awesome"}</Icon>}
          sx={{
            minHeight: { xs: "44px", sm: "48px" },
            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
            borderRadius: "12px",
            fontWeight: 600,
            textTransform: "none",
            ...(showProcessing && {
              borderStyle: "dashed",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 0.7 },
                "50%": { opacity: 1 },
              },
            }),
          }}
        >
          {showProcessing ? "Generating Draft..." : "Select for Draft"}
        </MDButton>
      </CardActions>
    </Card>
  );
}

RSSContentCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sourceHeadline: PropTypes.string.isRequired,
    sourceSummary: PropTypes.string,
    sourceURL: PropTypes.string,
    sourceChannel: PropTypes.string,
    date: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
};

export default RSSContentCard;
